import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../schema.dexie";
import { getCriancaAtiva, listarAlimentos } from "../db/queries";
import { calcularDerivados } from "../domain";
import {
  ESPECIALIDADE_LABEL,
  PASSO_ESCADA_LABEL,
  type Alimento,
  type EspecialidadeProf,
  type Oferta,
} from "../types";
import { StatusBadge } from "../ui/StatusBadge";
import { FoodPhoto } from "../ui/FoodPhoto";
import { EscadaTimeline } from "../ui/EscadaTimeline";
import { TendenciaLabel } from "../ui/Tendencia";
import { fraseEvolucao } from "../lib/evolucao";
import { baixarBinario, baixarCSV, ofertasParaCSV } from "../lib/export";
import { gerarSqlite } from "../lib/exportSqlite";
import { dataNascimentoFmt, diasAtras, idade } from "../lib/format";

/**
 * P0.7 — relatório legível por um profissional (nutri / TO / fono), sem jargão.
 * Visão de leitura na TELA + botão "gerar PDF" (impressão estilizada, client-side).
 * CSV continua disponível como export secundário de dados crus. Local-first: nada
 * sai do aparelho sem esta ação explícita do usuário (P0.8).
 */
const PERIODOS: { v: number | "tudo"; r: string }[] = [
  { v: 30, r: "últimos 30 dias" },
  { v: 90, r: "últimos 90 dias" },
  { v: 180, r: "últimos 6 meses" },
  { v: "tudo", r: "todo o histórico" },
];

export function Relatorio() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const alimentos = useLiveQuery(() => listarAlimentos(true)) ?? [];
  const ofertas = useLiveQuery(() => db.oferta.toArray()) ?? [];
  const [dias, setDias] = useState<number | "tudo" | "custom">("tudo");
  const [de, setDe] = useState(""); // YYYY-MM-DD
  const [ate, setAte] = useState(""); // YYYY-MM-DD
  const [feito, setFeito] = useState(false);
  const [exportandoDb, setExportandoDb] = useState(false);

  // limites do período (ms). custom: usa as datas (extremos abertos quando vazios).
  const inicioMs =
    dias === "custom"
      ? de
        ? new Date(`${de}T00:00:00`).getTime()
        : 0
      : dias === "tudo"
        ? 0
        : Date.now() - dias * 86400000;
  const fimMs =
    dias === "custom" && ate ? new Date(`${ate}T23:59:59.999`).getTime() : Infinity;
  const noPeriodo = (o: Oferta) => {
    const t = new Date(o.data_hora).getTime();
    return t >= inicioMs && t <= fimMs;
  };

  const ofertasPeriodo = ofertas.filter((o) => o.confirmado && noPeriodo(o));
  const ativos = alimentos.filter((a) => !a.arquivado);
  const comRegistro = ativos
    .filter((a) => ofertasPeriodo.some((o) => o.alimento_id === a.id))
    .sort((a, b) => a.nome.localeCompare(b.nome));

  const nSeguros = ativos.filter((a) => a.status === "seguro").length;
  const nTeste = ativos.filter((a) => a.status === "em_teste").length;

  // profissionais que acompanharam no período (registros de terapia, origem=profissional)
  const profissionais = (() => {
    const map = new Map<
      string,
      { nome: string | null; esp: EspecialidadeProf | null; n: number }
    >();
    for (const o of ofertasPeriodo) {
      if (o.origem !== "profissional") continue;
      const nome = o.profissional_nome ?? null;
      const esp = o.profissional_especialidade ?? null;
      const key = `${nome ?? ""}|${esp ?? ""}`;
      const cur = map.get(key) ?? { nome, esp, n: 0 };
      cur.n += 1;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => b.n - a.n);
  })();

  // alimentos que avançaram na escada dentro do período
  const avancaram = comRegistro.filter((a) => {
    const doAlim = ofertasPeriodo
      .filter((o) => o.alimento_id === a.id)
      .sort((x, y) => x.data_hora.localeCompare(y.data_hora));
    const primeiro = doAlim[0]?.passo_escada ?? 0;
    const max = Math.max(...doAlim.map((o) => o.passo_escada));
    return max > primeiro;
  }).length;

  const fmtData = (s: string) => new Date(`${s}T00:00:00`).toLocaleDateString("pt-BR");
  const periodoLabel =
    dias === "custom"
      ? de && ate
        ? `${fmtData(de)} a ${fmtData(ate)}`
        : de
          ? `desde ${fmtData(de)}`
          : ate
            ? `até ${fmtData(ate)}`
            : "período personalizado"
      : (PERIODOS.find((p) => p.v === dias)?.r ?? "período");
  const hojeFmt = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const nomePaciente = crianca?.nome_completo || crianca?.apelido || "paciente";
  const nascFmt = dataNascimentoFmt(crianca?.data_nascimento ?? null);
  const anos = idade(crianca?.data_nascimento ?? null);

  function gerarPdf() {
    window.print();
  }

  async function exportarCsv() {
    const csv = ofertasParaCSV(ofertas.filter(noPeriodo), alimentos);
    await baixarCSV(csv, `relatorio-alimentos-${new Date().toISOString().slice(0, 10)}.csv`);
    setFeito(true);
    setTimeout(() => setFeito(false), 2500);
  }

  // backup completo do banco em .sqlite (todo o histórico, não filtra por período)
  async function exportarSqlite() {
    setExportandoDb(true);
    try {
      const bytes = await gerarSqlite();
      await baixarBinario(
        bytes,
        `degrau-backup-${new Date().toISOString().slice(0, 10)}.sqlite`,
        "application/x-sqlite3",
      );
    } finally {
      setExportandoDb(false);
    }
  }

  return (
    <main className="tela relatorio">
      {/* controles — não entram no PDF */}
      <div className="no-print">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <h1 className="tela-titulo">relatório</h1>
        <p className="tela-sub">
          uma leitura clara da evolução, pra levar na consulta. nada sai do aparelho
          sem você gerar.
        </p>

        <div className="card">
          <div className="campo" style={{ marginBottom: dias === "custom" ? undefined : 0 }}>
            <label>período</label>
            <select
              value={String(dias)}
              onChange={(e) => {
                const v = e.target.value;
                setDias(v === "tudo" || v === "custom" ? v : Number(v));
              }}
            >
              {PERIODOS.map((p) => (
                <option key={String(p.v)} value={String(p.v)}>
                  {p.r}
                </option>
              ))}
              <option value="custom">período personalizado</option>
            </select>
          </div>

          {dias === "custom" && (
            <div className="linha-campos">
              <div className="campo" style={{ margin: 0 }}>
                <label>de</label>
                <input
                  type="date"
                  value={de}
                  max={ate || undefined}
                  onChange={(e) => setDe(e.target.value)}
                />
              </div>
              <div className="campo" style={{ margin: 0 }}>
                <label>até</label>
                <input
                  type="date"
                  value={ate}
                  min={de || undefined}
                  onChange={(e) => setAte(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <p className="secao-titulo" style={{ marginTop: "var(--s5)" }}>
          prévia do relatório
        </p>
      </div>

      {/* a folha — é o que vira PDF */}
      <article className="folha">
        <header className="rel-head">
          <div>
            <h2 className="rel-titulo">relatório de exposição alimentar</h2>
            <p className="rel-sub">
              {nomePaciente} · {periodoLabel}
            </p>
            {(nascFmt || anos !== null) && (
              <p className="rel-ident">
                nascimento: {nascFmt ?? "—"}
                {anos !== null ? ` · ${anos} ${anos === 1 ? "ano" : "anos"}` : ""}
              </p>
            )}
          </div>
          <div className="rel-data">gerado em {hojeFmt}</div>
        </header>

        <section className="rel-stats">
          <div className="rel-stat">
            <span className="n">{comRegistro.length}</span>
            <span className="r">alimentos trabalhados</span>
          </div>
          <div className="rel-stat">
            <span className="n">{ofertasPeriodo.length}</span>
            <span className="r">exposições</span>
          </div>
          <div className="rel-stat">
            <span className="n">{avancaram}</span>
            <span className="r">avançaram na escada</span>
          </div>
          <div className="rel-stat">
            <span className="n">{nSeguros}</span>
            <span className="r">seguros no total</span>
          </div>
        </section>

        {profissionais.length > 0 && (
          <section className="rel-profissionais">
            <h3 className="rel-secao-titulo">profissionais que acompanharam</h3>
            <ul className="rel-prof-lista">
              {profissionais.map((p, i) => (
                <li key={i}>
                  <span className="rel-prof-nome">{p.nome || "profissional"}</span>
                  {p.esp && <span className="rel-prof-esp"> · {ESPECIALIDADE_LABEL[p.esp]}</span>}
                  <span className="rel-prof-n">
                    {" "}
                    · {p.n} {p.n === 1 ? "sessão" : "sessões"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {comRegistro.length === 0 ? (
          <div className="vazio">
            sem exposições registradas neste período. registre algumas ofertas e o
            relatório se preenche.
          </div>
        ) : (
          <section className="rel-lista">
            {comRegistro.map((a) => (
              <RelatorioAlimento key={a.id} alimento={a} ofertas={ofertasPeriodo} />
            ))}
          </section>
        )}

        <footer className="rel-rodape">
          {(crianca?.responsavel_nome || crianca?.responsavel_cpf) && (
            <p className="rel-cpf">
              {crianca?.responsavel_nome
                ? `responsável (pais/guardião): ${crianca.responsavel_nome}`
                : ""}
              {crianca?.responsavel_nome && crianca?.responsavel_cpf ? " · " : ""}
              {crianca?.responsavel_cpf ? `CPF: ${crianca.responsavel_cpf}` : ""}
            </p>
          )}
          <p>
            escada de aproximação (0–9): não tolera na mesa · tolera na mesa · tolera no
            prato · toca · cheira · leva à boca · prova · mastiga e cospe · come um pouco
            · come a porção. subir um degrau já é progresso.
          </p>
          <p>
            gerado pelo Pitada (Degrau) — apoia o acompanhamento profissional, não o substitui.
          </p>
        </footer>
      </article>

      {/* exportar — sempre no fim da página (não entra no PDF) */}
      <div className="no-print" style={{ marginTop: "var(--s5)" }}>
        <p className="secao-titulo">exportar</p>
        <button
          className="btn btn-primario btn-bloco btn-grande"
          disabled={comRegistro.length === 0}
          onClick={gerarPdf}
        >
          🖨️ gerar PDF
        </button>
        <button
          className="btn btn-fantasma btn-bloco"
          style={{ marginTop: "var(--s2)" }}
          disabled={ofertasPeriodo.length === 0}
          onClick={exportarCsv}
        >
          exportar dados crus (csv)
        </button>
        <button
          className="btn btn-fantasma btn-bloco"
          style={{ marginTop: "var(--s2)" }}
          disabled={exportandoDb}
          onClick={exportarSqlite}
        >
          {exportandoDb ? "gerando banco…" : "🗄️ exportar banco completo (.sqlite)"}
        </button>
        {feito && (
          <p style={{ textAlign: "center", color: "var(--accent-forte)" }}>
            csv exportado ✓
          </p>
        )}
        <p className="meta" style={{ color: "var(--text-muted)", marginTop: "var(--s2)" }}>
          o .sqlite é um backup de todo o histórico (inclui fotos), pra guardar ou
          abrir em qualquer ferramenta SQLite.
        </p>
      </div>
    </main>
  );
}

function RelatorioAlimento({
  alimento,
  ofertas,
}: {
  alimento: Alimento;
  ofertas: Oferta[];
}) {
  const d = calcularDerivados(alimento.id, ofertas);
  const doAlim = ofertas.filter((o) => o.alimento_id === alimento.id);
  const sensorial = Object.values(alimento.sensorial).filter(Boolean);

  return (
    <div className="rel-card">
      <div className="rel-card-cab">
        <FoodPhoto fotoId={alimento.fotos[0]} nome={alimento.nome} categoria={alimento.categoria} />
        <div className="crescer">
          <div className="row-entre">
            <span className="rel-nome">{alimento.nome}</span>
            <StatusBadge status={alimento.status} />
          </div>
          {alimento.marca_produto && (
            <div className="meta">{alimento.marca_produto}</div>
          )}
          <div className="rel-card-meta">
            {d.n_ofertas} {d.n_ofertas === 1 ? "exposição" : "exposições"} ·{" "}
            {diasAtras(d.dias_desde_ultima)} · <TendenciaLabel tendencia={d.tendencia} />
          </div>
        </div>
      </div>

      <EscadaTimeline ofertas={doAlim} />
      <p className="evol-frase">{fraseEvolucao(alimento.nome, doAlim, d)}</p>

      <div className="meta" style={{ color: "var(--text-muted)" }}>
        maior passo:{" "}
        {d.passo_maximo === null ? "—" : PASSO_ESCADA_LABEL[d.passo_maximo]}
      </div>

      {sensorial.length > 0 && (
        <div className="chips" style={{ marginTop: "var(--s2)" }}>
          {sensorial.map((s) => (
            <span className="chip" key={s as string}>
              {s as string}
            </span>
          ))}
        </div>
      )}

      {alimento.observacoes && (
        <p className="rel-obs">“{alimento.observacoes}”</p>
      )}
    </div>
  );
}
